<template>
  <b-row>
    <b-col class="col-12">
      <b-card class="bg-white overflow-hidden col-lg-9">
        <template #header>
          <h1 class="text-center my-4">
            {{ formTitle }}
          </h1>
        </template>
        <validation-observer ref="observer">
          <b-form @submit.prevent="onSubmit">
            <div class="row">
              <div class="col col-6 col-md-2">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Title')" label-for="title-input">
                    <b-form-select
                      id="title-input"
                      v-model="title"
                      :state="getValidationState(dirty, validated, valid)"
                    >
                      <option value="" selected disabled hidden>
                        &nbsp;
                      </option>
                      <option>Mr</option>
                      <option>Mrs</option>
                      <option>Miss</option>
                      <option>Ms</option>
                      <option>Dr</option>
                    </b-form-select>
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-6 col-md-4">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('First Name')" label-for="forename-input">
                    <b-form-input
                      id="forename-input"
                      v-model="forename"
                      v-strip-url
                      :state="getValidationState(dirty, validated, valid)"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Last Name')" label-for="lastname-input">
                    <b-form-input
                      id="lastname-input"
                      v-model="surname"
                      v-strip-url
                      :state="getValidationState(dirty, validated, valid)"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
            </div>
            <div class="row">
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" :rules="`required|valid-phone-number:${country}`">
                  <b-form-group :label="$t('Phone')" label-for="phone-input">
                    <b-form-input
                      id="phone-input"
                      v-model="phone"
                      v-strip-url
                      type="tel"
                      :state="getValidationState(dirty, validated, valid)"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Company Name')" label-for="company-name-input">
                    <b-form-input
                      id="company-name-input"
                      v-model="companyName"
                      v-strip-url
                      :state="getValidationState(dirty, validated, valid)"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
            </div>
            <div class="row">
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Company Type')" label-for="company-type-input">
                    <b-form-select
                      id="company-type-input"
                      v-model="companyType"
                      :state="getValidationState(dirty, validated, valid)"
                    >
                      <optgroup v-for="(group, name) in companyTypes" :key="name" :label="name">
                        <option v-for="option in group" :key="option.value" :value="option.value">
                          {{ option.name }}
                        </option>
                      </optgroup>
                    </b-form-select>
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-12 col-md-6">
                <validation-provider v-if="shouldShowCompanyNumber" v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Company Number')">
                    <b-form-input v-model="companyNumber" v-strip-url :state="getValidationState(dirty, validated, valid)" />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
            </div>
            <div class="row">
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Geographic Coverage')" label-for="geographic-coverage-input">
                    <b-form-select
                      id="geographic-coverage-input"
                      v-model="geographicCoverage"
                      :state="getValidationState(dirty, validated, valid)"
                    >
                      <option v-for="option in geographicCoverageTypes" :key="option" :value="option">
                        {{ $t(option) }}
                      </option>
                    </b-form-select>
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Expected Lead Orders')" label-for="expected-lead-orders-input">
                    <b-form-select
                      id="expected-lead-orders-input"
                      v-model="expectedLeadOrders"
                      :state="getValidationState(dirty, validated, valid)"
                    >
                      <option v-for="option in expectedLeadOrdersTypes" :key="option" :value="option">
                        {{ $t(option) }}
                      </option>
                    </b-form-select>
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
            </div>
            <div class="row">
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required|email">
                  <b-form-group :label="$t('Email')" label-for="email-input">
                    <b-form-input
                      id="email-input"
                      v-model="email"
                      v-strip-url
                      type="email"
                      :state="getValidationState(dirty, validated, valid)"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Country')" label-for="country-input">
                    <b-form-select
                      id="country-input"
                      v-model="country"
                      :state="getValidationState(dirty, validated, valid)"
                    >
                      <option v-for="country in countries" :key="country.iso_code" :value="country.iso_code">
                        {{ country.name }}
                      </option>
                    </b-form-select>
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
            </div>
            <div v-if="canSelectLanguage" class="row">
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Communications Language')" label-for="email-input">
                    <b-form-select
                      id="comms-language"
                      v-model="commsLanguage"
                      :state="getValidationState(dirty, validated, valid)"
                    >
                      <option v-for="language in selectedCountry.languages" :key="language.iso_code" :value="language.iso_code">
                        {{ language.name }}
                      </option>
                    </b-form-select>
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Lead Languages')" label-for="lead-languages">
                    <b-form-select
                      id="lead-languages"
                      v-model="leadLanguages"
                      multiple
                      :state="getValidationState(dirty, validated, valid)"
                    >
                      <option v-for="language in selectedCountry.languages" :key="language.iso_code" :value="language.iso_code">
                        {{ language.name }}
                      </option>
                    </b-form-select>
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
            </div>
            <div class="row">
              <div class="col col-12 col-md-6">
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required|valid-password" vid="password">
                  <b-form-group :label="$t('Password')" label-for="password-input">
                    <b-form-input
                      id="password-input"
                      v-model="password"
                      type="password"
                      :state="getValidationState(dirty, validated, valid)"
                    />
                    <password v-model="password" :strength-meter-only="true" />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
                <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required|confirmed:password">
                  <b-form-group :label="$t('Confirm Password')" label-for="confirm-password-input">
                    <b-form-input
                      id="confirm-password-input"
                      v-model="confirmPassword"
                      type="password" :state="getValidationState(dirty, validated, valid)"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>
              <div class="col col-12 col-md-6">
                <validation-provider ref="addressValidator" v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                  <b-form-group :label="$t('Find Your Address')" label-for="postcode-input">
                    <address-finder
                      :country="country"
                      :show-address-input="true"
                      :state="getValidationState(dirty, validated, valid)"
                      @addressSelected="onAddressSelected"
                    />
                    <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                  <address v-if="addressFilled" class="font-weight-600 font-size-14">
                    {{ address1 }} <br>
                    {{ address2 }} <br v-if="address2">
                    {{ addressTown }} <br>
                    {{ addressCounty }}  <br>
                    {{ addressPostcode }}
                  </address>
                </validation-provider>
                <validation-provider ref="recaptchaValidator" v-slot="{errors, invalid}" rules="required">
                  <label class="invisible">{{ $t('Recaptcha') }}</label>
                  <div
                    class="g-recaptcha"
                    :data-sitekey="recaptchaKey"
                  />
                  <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-12">
                <validation-provider ref="termsValidator" v-slot="{validated, errors, dirty, valid, invalid}" :rules="{required: { allowFalse: false }}">
                  <b-form-checkbox
                    v-model="terms"
                    :state="getValidationState(dirty, validated, valid)"
                  >
                    <i18n path="registerAcceptTOS">
                      <template v-slot:tos>
                        <router-link :to="{name:'tnc'}" target="_blank" class="text-link font-weight-bold">
                          {{ $t('Terms & Conditions') }}
                        </router-link>
                      </template>
                    </i18n>
                  </b-form-checkbox>
                  <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </div>
            </div>
            <b-alert class="d-flex align-items-center" :show="errors.length > 0" variant="danger">
              <ul class="list-unstyled mb-0">
                <li v-for="(error, key) in errors" :key="key" v-html="error" />
              </ul>
            </b-alert>
            <div class="w-100 text-center">
              <b-button type="submit" :disabled="busy" variant="primary">
                {{ $t('Get Started') }}
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import AddressFinder from '@/components/form/AddressFinder';
import NotificationMixin from '@/imports/mixins/notification';

export default {
  name: "RegisterForm",
  components: {
    AddressFinder,
    ValidationProvider,
    ValidationObserver,
    Password: () => import('vue-password-strength-meter')
  },
  mixins: [NotificationMixin],
  props: {
    brand: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      busy: false,
      title: '',
      forename: '',
      surname: '',
      companyName: '',
      companyType: '',
      companyNumber: '',
      geographicCoverage: '',
      expectedLeadOrders: '',
      phone: '',
      email: '',
      password: '',
      confirmPassword: '',
      recaptchaScriptTag: null,
      addressFilled: false,
      address1: '',
      address2: '',
      addressTown: '',
      addressCounty: '',
      addressPostcode: '',
      errors: [],
      terms: false,
      countries: [],
      country: 'gb',
      commsLanguage: '',
      leadLanguages: [],
    }
  },
  computed: {
    recaptchaKey() {
      return process.env.VUE_APP_RECAPTCHA_KEY;
    },
    shouldShowCompanyNumber() {
      const typesToShow = ['Ltd', 'Plc', 'CIC', 'PrivateLtd', 'Unlimited'];
      return typesToShow.includes(this.companyType);
    },
    formTitle() {
      return this.brand?.registerFormHeading ?? this.$t('Sign Up Today!');
    },
    companyTypes() {
      return {
        [this.$t('Standard Types')]: [
          {value:"Ltd", name: this.$t('Ltd (Private Limited Company)')},
          {value:"SoleTrader", name: this.$t('Sole Trader')},
          {value:"Partnership", name: this.$t('General Partnership')},
          {value:"Plc", name: this.$t('Plc (Public Limited Company)')},
        ],
        [this.$t('Other Types')]: [
          {value:'CIC', name: this.$t('CIC (Community Interest Company)')},
          {value:'Coop', name: this.$t('Co-operative')},
          {value:'LLP', name: this.$t('LLP (Limited Liability Partnership)')},
          {value:'LP', name: this.$t('LP (Limited Partnership)')},
          {value:'PrivateLtd', name: this.$t('Private Company Limited by Guarantee')},
          {value:'Unlimited', name: this.$t('Unlimited Company')},
          {value:'Other', name: this.$t('Other (we will ask you about this)')},
        ]
      }
    },
    geographicCoverageTypes() {
      return [
        'Local',
        'Regional',
        'National',
      ];
    },
    expectedLeadOrdersTypes() {
      return [
        '100 to 150 per Month',
        '151 to 200 per Month',
        '201 to 250 per Month',
        '251 to 300 per Month',
        '301 to 350 per Month',
        '350+ per Month',
      ];
    },
    selectedCountry() {
      return Object.values(this.countries).find((country) => country.iso_code === this.country);
    },
    canSelectLanguage() {
      return this.selectedCountry?.languages?.length > 1;
    }
  },
  methods: {
    onSubmit() {
      const recaptchaResponse = document.querySelector('.g-recaptcha-response').value;
      this.$refs.recaptchaValidator.syncValue(recaptchaResponse);
      this.$refs.observer.validate().then(success => {

        if(!success || (recaptchaResponse === '')) {
          this.scrollToInvalidField();
          window.grecaptcha.reset();
          return;
        }

        this.busy = true;
        this.$api.company.register({
          recaptcha: recaptchaResponse,
          title: this.title,
          fname: this.forename,
          sname: this.surname,
          phone: this.phone,
          email: this.email.trim(),
          username: this.email.trim(),
          password: this.password,
          organisation: this.companyName,
          companytype: this.companyType,
          companieshouseno: this.companyNumber,
          geographicCoverage: this.geographicCoverage,
          expectedLeadOrders: this.expectedLeadOrders,
          address1: this.address1,
          address2: this.address2 ?? '',
          address3: this.addressTown,
          postcode: this.addressPostcode,
          county: this.addressCounty,
          tnc: this.terms,
          jref: document.referrer,
          brand: this.brand?.key ?? null,
          lang: this.brand?.language ?? 'en',
          region: this.brand?.region ?? 'gb',
          country: this.country,
          commsLanguage: this.commsLanguage,
          leadLanguages: this.leadLanguages,
        })
          .then(() => {
            this.$router.push({name: 'dashboard', params: {fromPage: 'registration'}});
          })
          .catch((error) => {
            this.errors = error.response.data.errors || [this.$t('There was an error registering your company')];
            this.notify(this.$bvToast, this.$t('There was an error registering your company'), null, 'danger');
            window.grecaptcha.reset();
          })
          .finally(() => this.busy = false);
      });
    },
    onAddressSelected({line1, line2, town, county, postcode}) {
      this.address1 = line1;
      this.address2 = line2;
      this.addressTown = town;
      this.addressCounty = county;
      this.addressPostcode = postcode;

      this.$refs.addressValidator.syncValue([line1, line2, town, county, postcode].join());
      this.$refs.addressValidator.validate();
      this.addressFilled = true;
    },
    scrollToInvalidField() {
      const errors = Object.entries(this.$refs.observer.refs)
        .map(([key, value]) => ({key, value}))
        .filter((error) => {
          if (!error || !error.value || !error.value.failedRules) {
            return false;
          }
          return Object.keys(error.value.failedRules).length > 0;
        });
      if (errors && errors.length > 0) {
        this.$refs.observer.refs[errors[0]['key']].$el.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    },
    getValidationState(dirty, validated, valid) {
      if (dirty || validated) {
        return valid ? null : false;
      }
      return null;
    },
  },
  watch: {
    selectedCountry() {
      if (!this.canSelectLanguage) {
        this.commsLanguage = this.selectedCountry.languages[0].iso_code;
        this.leadLanguages = [this.selectedCountry.languages[0].iso_code];
      }
    }
  },
  mounted() {
    let recaptchaScript = document.createElement('script');
    recaptchaScript.setAttribute('src', 'https://www.google.com/recaptcha/api.js');
    document.head.appendChild(recaptchaScript);
    this.recaptchaScriptTag = recaptchaScript;
    if (this.$route.query.email) {
      this.email = this.$route.query.email;
    }
    let lang = this.$route.query.lang;
    if (lang) {
      this.$i18n.locale = lang;
    }
    this.$api.country.fetchAll().then((result) => {
      this.countries = result.data.data[0];
    });
  },
  destroyed() {
    if (this.recaptchaScriptTag !== null) {
      this.recaptchaScriptTag.remove();
    }
  },
}
</script>

<style scoped lang="scss">
.card {
  box-shadow: 0 0 1.875rem rgba(0,0,0,.16);
  max-width: 768px;
  border-radius: 48px;
  padding: 0;

  .card-header {
    background-color: $white;
  }

  .card-body {
    @include media-breakpoint-down(sm) {
      margin: 10px 25px 30px;
    }
    @include media-breakpoint-up(sm) {
      margin: 10px 60px 30px;
    }
  }

  @include media-breakpoint-up(md) {
    margin: -235px auto 0;
  }

  @include media-breakpoint-down(sm) {
    margin: -175px auto 0;
  }

  h2 {
    font-size: 36px;

    @include media-breakpoint-up(sm) {
      margin: 42px auto 35px;
    }

    @include media-breakpoint-down(sm) {
      margin: 20px auto 20px;
    }
  }
  ::v-deep .custom-control-label {
    &::before {
      border: 2px solid #d4dae1 !important;
    }
    .text-link {
      color: $theme-secondary;
      &:hover {
        color: $theme-secondary;
      }
    }
  }
}

.invalid-feedback {
  color: $register-invalid-feedback;
  font-weight: $font-weight-500;
}

.g-recaptcha {
  transform:scale(0.77);
  transform-origin:0 0;
}

</style>
